/**
 * Trust badges carousel — mobile-only swipe with autoplay and dot indicators.
 *
 * On desktop (>549px) this component does nothing: no observers, no timers,
 * no event listeners. Everything activates lazily via matchMedia so there is
 * zero PageSpeed cost on desktop.
 *
 * On mobile it uses:
 *  - CSS scroll-snap for native swipe (no JS touch handling)
 *  - IntersectionObserver to sync dots (no scroll listeners)
 *  - setInterval for autoplay, paused on touch / visibility change
 */

const MOBILE_QUERY = '(max-width: 549px)';

class TrustBadgesComponent extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | undefined} */
  #interval = undefined;

  /** @type {number} */
  #current = 0;

  /** @type {MediaQueryList} */
  #mql;

  /** @type {boolean} */
  #active = false;

  connectedCallback() {
    this.#mql = window.matchMedia(MOBILE_QUERY);
    this.#mql.addEventListener('change', this.#handleMediaChange);
    if (this.#mql.matches) this.#activate();
  }

  disconnectedCallback() {
    this.#deactivate();
    this.#mql.removeEventListener('change', this.#handleMediaChange);
  }

  /* ---- Media query gate ---- */

  #handleMediaChange = (e) => {
    e.matches ? this.#activate() : this.#deactivate();
  };

  /* ---- Lifecycle ---- */

  #activate() {
    if (this.#active) return;
    this.#active = true;

    const track = this.querySelector('.trust-badges__track');
    const items = track?.querySelectorAll('.trust-badges__item');
    if (!track || !items?.length) return;

    /* Dot click → scroll to slide */
    this.addEventListener('click', this.#handleDotClick);

    /* IntersectionObserver on each item to sync dots */
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 0.5) {
            const index = [...items].indexOf(entry.target);
            if (index !== -1) this.#setCurrent(index);
          }
        }
      },
      { root: track, threshold: 0.5 }
    );

    items.forEach((item) => this.#observer?.observe(item));

    /* Touch → pause autoplay */
    track.addEventListener('touchstart', this.#pause, { passive: true });
    track.addEventListener('touchend', this.#resume, { passive: true });

    /* Visibility → pause when tab hidden */
    document.addEventListener('visibilitychange', this.#handleVisibility);

    this.#play();
  }

  #deactivate() {
    if (!this.#active) return;
    this.#active = false;

    this.#stop();
    this.#observer?.disconnect();
    this.#observer = null;

    const track = this.querySelector('.trust-badges__track');
    track?.removeEventListener('touchstart', this.#pause);
    track?.removeEventListener('touchend', this.#resume);

    this.removeEventListener('click', this.#handleDotClick);
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  /* ---- Dot sync ---- */

  #setCurrent(index) {
    if (index === this.#current) return;
    this.#current = index;

    const dots = this.querySelectorAll('.trust-badges__dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('trust-badges__dot--active', i === index);
    });
  }

  /**
   * Scrolls the track to a specific child by index without affecting page scroll.
   * @param {number} index
   */
  #scrollToSlide(index) {
    const track = this.querySelector('.trust-badges__track');
    if (!track) return;

    const item = track.children[index];
    if (!item) return;

    const trackWidth = track.clientWidth;
    const itemWidth = item.clientWidth;
    const itemLeft = /** @type {HTMLElement} */ (item).offsetLeft;

    track.scrollTo({
      left: itemLeft - (trackWidth - itemWidth) / 2,
      behavior: 'smooth',
    });
  }

  /** @param {Event} e */
  #handleDotClick = (e) => {
    const dot = /** @type {HTMLElement} */ (e.target).closest('.trust-badges__dot');
    if (!dot) return;

    const index = Number(dot.dataset.index);
    if (Number.isNaN(index)) return;

    this.#scrollToSlide(index);

    /* Reset autoplay timer after manual interaction */
    this.#stop();
    this.#play();
  };

  /* ---- Autoplay ---- */

  get #autoplayMs() {
    const val = parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(val) ? 0 : val * 1000;
  }

  #play() {
    const ms = this.#autoplayMs;
    if (!ms) return;

    this.#interval = setInterval(() => {
      if (document.hidden) return;

      const track = this.querySelector('.trust-badges__track');
      const count = track?.children.length ?? 0;
      if (count < 2) return;

      const next = (this.#current + 1) % count;
      this.#scrollToSlide(next);
    }, ms);
  }

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #pause = () => this.#stop();

  #resume = () => {
    this.#stop();
    this.#play();
  };

  #handleVisibility = () => {
    document.hidden ? this.#stop() : this.#resume();
  };
}

if (!customElements.get('trust-badges-component')) {
  customElements.define('trust-badges-component', TrustBadgesComponent);
}
